import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Search,
  Lightbulb,
  ClipboardCheck,
  FileText,
  TrendingUp,
  Building2,
  Sparkles,
  Layers,
  Scale,
  Shield,
  Users,
  Compass,
  CheckCircle2,
  Globe,
  AlertTriangle,
  Briefcase,
} from 'lucide-react';
import './About.css';

// Supporting stats without unwanted icons
const supportingStats = [
  {
    sub: 'अभियान घोषणा',
    val: '२०८२ माघ २९',
    label: 'सुरुआत मिति',
  },
  {
    sub: 'नगर रूपान्तरण',
    val: '२० क्षेत्र',
    label: 'विकास प्राथमिकता',
  },
  {
    sub: 'नागरिक नेतृत्व',
    val: '१००% स्वयंसेवी',
    label: 'स्वतःस्फूर्त सहभागिता',
  },
];

const objectives = [
  {
    title: 'दिगो विकासको खाका',
    desc: 'नगरका वास्तविक आवश्यकतामा आधारित २५ वर्षे रणनीतिक मार्गचित्र।',
    icon: Layers,
  },
  {
    title: 'सहज जीवनयापन',
    desc: 'प्रत्येक नगरवासीका लागि गुणस्तरीय सेवा, स्वच्छ वातावरण र सुविधा।',
    icon: Sparkles,
  },
  {
    title: 'रोजगारी र लगानी',
    desc: 'स्थानीय उद्यमशीलता, युवा रोजगारी र दिगो आर्थिक केन्द्रको विकास।',
    icon: TrendingUp,
  },
  {
    title: 'समावेशी विकास',
    desc: 'सबै वडा, समुदाय र भूगोललाई समेट्ने समन्यायिक विकास प्रणाली।',
    icon: Scale,
  },
  {
    title: 'साझा एकता',
    desc: 'दलीय वा व्यक्तिगत भिन्नताभन्दा माथि उठेर समृद्धिका लागि सहकार्य।',
    icon: Users,
  },
  {
    title: 'सुशासन र पारदर्शिता',
    desc: 'पारदर्शी निर्णय प्रक्रिया र प्रत्यक्ष नागरिक सहभागिताको प्रत्याभूति।',
    icon: Shield,
  },
];

const processSteps = [
  { num: '०१', title: 'क्षेत्र पहिचान', desc: '२० वटा मुख्य प्राथमिकता क्षेत्रको निर्धारण', icon: Search },
  { num: '०२', title: 'विचार संकलन', desc: 'खुला नागरिक सुझाव र जनमत संकलन', icon: Lightbulb },
  { num: '०३', title: 'समीक्षा र परामर्श', desc: 'तथ्याङ्क विश्लेषण र विज्ञहरूको परामर्श', icon: ClipboardCheck },
  { num: '०४', title: 'योजना प्रकाशन', desc: 'अल्पकालीन र दीर्घकालीन योजना सार्वजनिक', icon: FileText },
  { num: '०५', title: 'पहल र अनुगमन', desc: 'कार्यान्वयनका लागि निरन्तर पैरवी र निगरानी', icon: TrendingUp },
  { num: '०६', title: 'साझा निर्माण', desc: 'नागरिक सहकार्यबाट समृद्ध इटहरी निर्माण', icon: Building2 },
];

const About = () => {
  return (
    <main className="about-page page-enter">
      {/* ── 1. Hero Section ── */}
      <section className="about-hero relative overflow-hidden py-12 md:py-20 border-b border-char-100/80">
        <div className="about-hero__grid-pattern" />
        <div className="pointer-events-none absolute left-[-80px] top-[-60px] h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute right-[-60px] bottom-[-40px] h-60 w-60 rounded-full bg-amber-500/10 blur-3xl" />

        <div className="container relative z-10 mx-auto px-5 max-w-6xl">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            {/* Left Column: Heading & Description */}
            <div className="lg:col-span-7">
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-char-900 tracking-tight">
                भोलिको इटहरी <span className="text-primary">– भिजन २०५०</span>
              </h1>

              <p className="mt-5 text-base leading-relaxed text-char-700">
                भोलिको इटहरी–भिजन २०५० नागरिक तहबाट सुरु भएको एउटा <strong>स्वतःस्फूर्त स्वयंसेवी अभियान</strong> हो, जुन २०८२ माघ २९ गतेदेखि सुरु गरिएको हो। कुनै औपचारिक कमिटी वा सरकारी निकायको भर नपरी नगरवासीकै प्रत्यक्ष सहभागितामा इटहरीलाई आगामी २५ वर्ष (सन् २०५०) भित्र विश्वस्तरीय शहर बनाउन २० प्राथमिकता क्षेत्रमा योजना तर्जुमा भइरहेको छ।
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3.5">
                <Link
                  to="/sectors"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-extrabold text-white shadow-green transition-all duration-300 hover:bg-primary-500 hover:shadow-green-hover hover:-translate-y-0.5"
                >
                  प्राथमिकता क्षेत्रहरू <ArrowRight size={16} />
                </Link>
                <Link
                  to="/volunteer"
                  className="inline-flex items-center gap-2 rounded-xl border border-char-200 bg-white px-6 py-3.5 text-sm font-extrabold text-char-700 shadow-xs transition-all duration-300 hover:border-primary-300 hover:text-primary hover:bg-primary-50/50 hover:-translate-y-0.5"
                >
                  अभियानमा जोडिनुहोस्
                </Link>
              </div>
            </div>

            {/* Right Column: Hierarchical Vision Card (1 Highlighted + 3 Supporting, No Unwanted Icons) */}
            <div className="lg:col-span-5">
              <div className="vision-card">
                {/* Header */}
                <div className="vision-card__header">
                  <div>
                    <h3 className="vision-card__title">
                      संक्षिप्त परिचय
                    </h3>
                    <p className="vision-card__subtitle">
                      Bholiko Itahari Vision 2050
                    </p>
                  </div>
                </div>

                {/* 1. HIGHLIGHTED FEATURE: The Core Long-term Vision */}
                <div className="vision-highlight">
                  <span className="vision-highlight__tag">दीर्घकालीन मार्गचित्र</span>
                  <div className="vision-highlight__val font-display">
                    सन् २०५०
                  </div>
                  <p className="vision-highlight__desc">
                    २५ वर्षे लक्ष्य — विश्वस्तरीय शहर निर्माण
                  </p>
                </div>

                {/* 2. SUPPORTING ITEMS: Clean 3-column row without icons */}
                <div className="vision-supporting-grid">
                  {supportingStats.map((item, i) => (
                    <div key={i} className="vision-supporting-item">
                      <span className="vision-supporting-item__sub">{item.sub}</span>
                      <p className="vision-supporting-item__val font-display">{item.val}</p>
                      <p className="vision-supporting-item__label">{item.label}</p>
                    </div>
                  ))}
                </div>

                {/* 3. Bottom Quote */}
                <div className="vision-card__quote">
                  <p className="vision-card__quote-text">
                    “सबै वर्ग, समुदाय र विचारका नगरवासी विकासको सवालमा एकाकार।”
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. अभियानको औचित्य र योजना निर्माण (Compact Overview) ── */}
      <section className="py-12 md:py-16 bg-white border-b border-char-100/80">
        <div className="container mx-auto px-5 max-w-6xl">
          <div className="grid gap-8 md:grid-cols-2">
            {/* Left: औचित्य */}
            <div className="rounded-3xl border border-amber-200 bg-amber-50/40 p-6 sm:p-7">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-amber-800 uppercase tracking-wider mb-3">
                <AlertTriangle size={15} />
                <span>किन आवश्यकता पर्यो?</span>
              </div>
              <h2 className="text-xl font-extrabold text-char-900 font-display">
                दीर्घकालीन योजनाको खाँचो
              </h2>
              <p className="mt-3 text-sm text-char-600 leading-relaxed">
                वर्षौँ पुराना समस्या, अनुपलब्धीमूलक खर्च र वित्तीय संकटका बीच नगरका वास्तविक प्राथमिकताहरू ओझेलमा परे। व्यापारिक सम्भावना हुँदाहुँदै पनि दीर्घकालीन सोच र तथ्याङ्क व्यवस्थापनको अभाव खटकियो।
              </p>
              <ul className="mt-4 space-y-2 text-xs text-char-700 font-medium">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
                  नागरिकका प्राथमिकता अनुसार विकास निर्देशित गर्न
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
                  लगानी, अनुदान र रोजगारी आकर्षित गर्न
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
                  तत्काल र दीर्घकालीन समस्याहरूको दिगो समाधान खोज्न
                </li>
              </ul>
            </div>

            {/* Right: योजना निर्माण विधि */}
            <div className="rounded-3xl border border-primary-200 bg-primary-50/30 p-6 sm:p-7">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wider mb-3">
                <Briefcase size={15} />
                <span>योजना निर्माण विधि</span>
              </div>
              <h2 className="text-xl font-extrabold text-char-900 font-display">
                वैज्ञानिक तथा सहभागितामूलक प्रक्रिया
              </h2>
              <p className="mt-3 text-sm text-char-600 leading-relaxed">
                नगरवासीका सपना र सम्भावनाहरूलाई एकाकार गर्दै तथ्यपरक खाका बनाइँदैछ। योजना बनेपछि कार्यान्वयनका लागि सम्बन्धित निकायलाई रचनात्मक दबाब र निरन्तर पैरवी गरिनेछ।
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-semibold text-char-800">
                <div className="bg-white p-2.5 rounded-xl border border-char-100 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-primary shrink-0" /> सरोकारवाला छलफल
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-char-100 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-primary shrink-0" /> स्थलगत सर्वेक्षण
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-char-100 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-primary shrink-0" /> तथ्य/तथ्याङ्क विश्लेषण
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-char-100 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-primary shrink-0" /> विज्ञ परामर्श र सहकार्य
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. हाम्रो उद्देश्यहरू (Core Objectives) ── */}
      <section className="py-12 md:py-16 bg-cream border-b border-char-100/80">
        <div className="container mx-auto px-5 max-w-6xl">
          <div className="text-center max-w-xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-char-900 font-display">
              हाम्रा मुख्य उद्देश्यहरू
            </h2>
            <p className="mt-2 text-sm text-char-600">
              भोलिको इटहरी निर्माणका ६ आधारभूत स्तम्भहरू
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {objectives.map((obj, i) => {
              const Icon = obj.icon;
              return (
                <div key={i} className="rounded-2xl border border-char-100 bg-white p-5 shadow-xs transition-all duration-300 hover:border-primary-300 hover:shadow-card hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-primary bg-primary-50 px-2.5 py-1 rounded-lg">
                      ०{i + 1}
                    </span>
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-100 text-primary">
                      <Icon size={18} />
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-char-900">{obj.title}</h3>
                  <p className="mt-1.5 text-xs text-char-500 leading-relaxed">{obj.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 4. अभियान कसरी अघि बढ्छ (Workflow) ── */}
      <section className="py-12 md:py-16 bg-white border-b border-char-100/80">
        <div className="container mx-auto px-5 max-w-6xl">
          <div className="text-center max-w-xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-char-900 font-display">
              अभियान कसरी अघि बढ्छ?
            </h2>
            <p className="mt-2 text-sm text-char-600">
              विचार संकलनदेखि दीर्घकालीन कार्यान्वयनसम्मको ६ चरण
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="flex items-start gap-3.5 rounded-2xl border border-char-100 bg-cream-50/50 p-4 transition-all duration-300 hover:bg-white hover:border-primary-300 hover:shadow-xs">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-white text-xs font-bold">
                    {step.num}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Icon size={14} className="text-primary" />
                      <h3 className="text-sm font-bold text-char-900">{step.title}</h3>
                    </div>
                    <p className="mt-1 text-xs text-char-500 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 5. Call To Action & Motto ── */}
      <section
        className="py-14 px-6 text-center text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #15803d 60%, #166534 100%)' }}
      >
        <div className="max-w-xl mx-auto relative z-10">
          <p className="text-xs font-bold tracking-widest text-amber-300 mb-2 uppercase">
            Think Global, Act Local
          </p>

          <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight font-display">
            अब बन्छ इटहरी, हामी बनाउँछौं इटहरी
          </h2>

          <p className="mt-3 text-xs sm:text-sm text-white/85 leading-relaxed">
            आफ्नो सुझाव, विचार वा स्वयंसेवामार्फत इटहरीको भविष्य निर्माण अभियानमा जोडिनुहोस्।
          </p>

          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-xs sm:text-sm font-bold text-green-900 shadow-md transition-all duration-300 hover:bg-green-50 hover:-translate-y-0.5"
            >
              स्वयंसेवक बन्नुहोस् <ArrowRight size={14} />
            </Link>
            <Link
              to="/contributions"
              className="inline-flex items-center gap-2 rounded-xl border border-amber-400/80 bg-amber-500 px-5 py-2.5 text-xs sm:text-sm font-bold text-slate-950 shadow-sm transition-all duration-300 hover:bg-amber-400 hover:-translate-y-0.5"
            >
              सुझाव / योगदान दिनुहोस्
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
